import type { Doc } from "building-types/doc";
import type { FloorAssemblyOverride } from "building-types/floors/floorAssemblyTypes";
import { StructuralFloorType } from "building-types/floors/floorComponentsTypes";
import type { FloorSystem } from "building-types/floors/floorSystemTypes";
import { FloorType } from "building-types/floors/floorTypes";
import { floorFramingComponentTypeFromJoistPlacement } from "building-types/framing/floorFramingComponentTypes";
import type {
  LumberRailMemberInfo,
  LumberRailMemberInfoFloor,
  LumberRailMemberInfoWall,
} from "building-types/framing/lumberRailMemberInfoTypes";
import { LumberRailMemberInfoSource } from "building-types/framing/lumberRailMemberInfoTypes";
import type { LumberRail } from "building-types/framing/lumberTypes";
import type { GeneratedFramedFloorAssembly } from "building-types/horizontalAssemblies/generatedFloorAssemblyTypes";
import type { WallFraming, WallFramingMeta } from "building-types/walls/wallFramingTypes";
import type { Table, RWTable } from "common/data/table";
import { getFloorIdForBay } from "doc/elements/floor/floorAssembly";

/**
 * Indexes all rails from a framed floor assembly into global tables (same rail references as on members).
 */
export function collectLumberRailsFromFramedFloorAssembly(
  assembly: GeneratedFramedFloorAssembly,
  lumberRails: RWTable<LumberRail>,
  lumberRailToMemberInfo: RWTable<LumberRailMemberInfo>,
  floors?: Table<FloorAssemblyOverride>,
): void {
  const sourceHandles: NonNullable<LumberRailMemberInfoFloor["sourceHandles"]> =
    assembly.bayId && floors && getFloorIdForBay(floors, assembly.bayId)
      ? [
          { type: "FLOOR_SYSTEM", id: assembly.floorSystemId },
          { type: "FLOOR_BAY", id: assembly.bayId },
        ]
      : [{ type: "FLOOR_SYSTEM", id: assembly.floorSystemId }];

  for (const members of assembly.framing) {
    for (const member of members) {
      const info: LumberRailMemberInfoFloor = {
        source: LumberRailMemberInfoSource.FLOOR,
        memberType: member.type,
        profileDescriptorName: member.profileDescriptor.name,
        sourceHandles,
      };
      for (const rail of member.rails) {
        lumberRails[rail.id] = rail;
        lumberRailToMemberInfo[rail.id] = info;
      }
    }
  }
}

export function collectLumberRailsFromAllFramedFloorAssemblies(
  framedFloorAssemblies: Table<GeneratedFramedFloorAssembly>,
  lumberRails: RWTable<LumberRail>,
  lumberRailToMemberInfo: RWTable<LumberRailMemberInfo>,
  floors?: Table<FloorAssemblyOverride>,
): void {
  for (const assemblyId in framedFloorAssemblies) {
    collectLumberRailsFromFramedFloorAssembly(
      framedFloorAssemblies[assemblyId],
      lumberRails,
      lumberRailToMemberInfo,
      floors,
    );
  }
}

export function collectLumberRailsFromFloorSystems(
  floorSystems: Table<FloorSystem>,
  lumberRails: RWTable<LumberRail>,
  lumberRailToMemberInfo: RWTable<LumberRailMemberInfo>,
): void {
  for (const levelId in floorSystems) {
    const floorSystem = floorSystems[levelId];
    for (const roomClassFloors of floorSystem.roomClassFloors) {
      for (const floor of roomClassFloors) {
        if (floor.floorType !== FloorType.SOLID || floor.structuralType !== StructuralFloorType.FRAMING) {
          continue;
        }

        const sourceHandle: NonNullable<LumberRailMemberInfoFloor["sourceHandles"]>[number] = {
          type: "GENERATED_ENTITY",
          entity: {
            entityType: floor.entityType,
            id: floor.id,
          },
        };

        for (const framing of floor.floorFraming) {
          const { extrusionFramingProfile } = framing;
          const { profileDescriptor, railsByType } = framing.joists;
          for (const type of Object.keys(railsByType)) {
            const memberType = floorFramingComponentTypeFromJoistPlacement(type);
            if (memberType === undefined) {
              continue;
            }
            const info: LumberRailMemberInfoFloor = {
              source: LumberRailMemberInfoSource.FLOOR,
              memberType,
              profileDescriptorName: extrusionFramingProfile?.name ?? profileDescriptor.name,
              sourceHandles: [sourceHandle],
            };
            for (const rail of railsByType[type]) {
              lumberRails[rail.id] = rail;
              lumberRailToMemberInfo[rail.id] = info;
            }
          }
        }
      }
    }
  }
}

function getWallFramingSourceHandles(
  meta: WallFramingMeta | undefined,
  openings: Doc["openings"],
): LumberRailMemberInfoWall["sourceHandles"] {
  const sourceHandles: Array<NonNullable<LumberRailMemberInfoWall["sourceHandles"]>[number]> = [];
  if (meta?.sourceHandle !== undefined) {
    sourceHandles.push(meta.sourceHandle);
  }
  if (meta?.openingHandle !== undefined && openings[meta.openingHandle.id] !== undefined) {
    sourceHandles.push(meta.openingHandle);
  }
  return sourceHandles.length > 0 ? sourceHandles : undefined;
}

/**
 * Indexes wall framing rails from one level's `WallFraming[]`.
 */
export function collectLumberRailsFromWallFramingList(
  wallFramings: readonly WallFraming[],
  openings: Doc["openings"],
  lumberRails: RWTable<LumberRail>,
  lumberRailToMemberInfo: RWTable<LumberRailMemberInfo>,
): void {
  for (const wallFraming of wallFramings) {
    for (const framing of wallFraming.framings) {
      const sourceHandles = getWallFramingSourceHandles(framing.meta, openings);
      const info: LumberRailMemberInfoWall = {
        source: LumberRailMemberInfoSource.WALL,
        memberType: framing.type,
        profileDescriptorName: framing.profileDescriptor.name,
        ...(framing.meta?.headerAssemblyName !== undefined
          ? { headerAssemblyName: framing.meta.headerAssemblyName }
          : {}),
        ...(sourceHandles !== undefined ? { sourceHandles } : {}),
      };
      // Header rails for the same opening share a single ID (for group selection);
      // lumberRails[id] will hold only the last-written rail for grouped headers.
      for (const rail of framing.rails) {
        lumberRails[rail.id] = rail;
        lumberRailToMemberInfo[rail.id] = info;
      }
    }
  }
}

export function collectLumberRailsFromWallFramingSystems(
  wallFramingSystems: Table<readonly WallFraming[]>,
  openings: Doc["openings"],
  lumberRails: RWTable<LumberRail>,
  lumberRailToMemberInfo: RWTable<LumberRailMemberInfo>,
): void {
  for (const levelId in wallFramingSystems) {
    const list = wallFramingSystems[levelId];
    if (list) {
      collectLumberRailsFromWallFramingList(list, openings, lumberRails, lumberRailToMemberInfo);
    }
  }
}
